import { useState } from 'react';
import http from 'axios';
import { SignInCredentials, SignInResponse } from '../types';
import { loginHandler, sendPinCodeHandler, sendQrPinCodeHandler, SignInResponseCode } from './utils';
import { DEFAULT_ERROR_MESSAGE, getErrorMessage } from 'utils/gql';
import { useNavigate } from 'react-router-dom';
import { APIS } from 'config';
import { ErrorCodes } from 'commonTypes';

const BASE_URL = APIS.AUTHN;

export default function useAuthSass() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>('');
  const [errorCode, setErrorCode] = useState<ErrorCodes | undefined>();
  const [isSuccess, setSuccess] = useState(false);
  const navigate = useNavigate();

  const openRequest = () => {
    setLoading(true);
    setError('');
    setErrorCode(undefined);
  };

  const closeRequest = (err: string, errorCode?: ErrorCodes) => {
    setLoading(false);
    setError(err);
    errorCode && setErrorCode(errorCode);
  };

  const clearError = () => {
    setError('');
    setErrorCode(undefined);
  };

  const login = async ({ password, email }: SignInCredentials): Promise<void> => {
    try {
      openRequest();
      const { data } = await http.post<SignInResponse>(
        `${BASE_URL}/auth/signin`,
        { password, email },
        { withCredentials: true }
      );

      await loginHandler({ ...data, email }, navigate);
      if ([SignInResponseCode.Authenticated, SignInResponseCode.SsoRedirect].includes(data.code)) {
        clearError();
      } else {
        closeRequest('');
      }
    } catch (err) {
      if (http.isAxiosError(err)) {
        if (!password && err.response?.status === 401) {
          closeRequest('');
          throw err;
        } else {
          closeRequest(err.response?.data.message);
        }
      }
    }
  };

  const sendQrPinCode = async ({ qrPinText }: SignInCredentials) => {
    try {
      openRequest();
      const response = await http.post(`${BASE_URL}/mfa/authenticate`, { pinCode: qrPinText });
      await sendQrPinCodeHandler(navigate, response.data);
      closeRequest('');
    } catch (err) {
      if (http.isAxiosError(err)) {
        closeRequest(err.response?.data.message);
      }
    }
  };

  const sendPinCode = async ({ phonePinText }: SignInCredentials) => {
    try {
      openRequest();
      const { data } = await http.post<SignInResponse>(`${BASE_URL}/mfa/validate`, { pinCode: phonePinText });
      sendPinCodeHandler({ ...data }, navigate);
      closeRequest('');
    } catch (err) {
      if (http.isAxiosError(err)) {
        closeRequest(err.response?.data.message);
      }
    }
  };

  const sendResetPassword = async ({ email }: SignInCredentials) => {
    try {
      openRequest();
      const responseData = await http.post(`${BASE_URL}/passwordreset/request`, { email });
      setSuccess(true);
      closeRequest('');
      return { data: responseData, success: true };
    } catch (err) {
      setSuccess(false);
      if (http.isAxiosError(err)) {
        const errorMessage = getErrorMessage(err.response?.data.errorCode);
        closeRequest(errorMessage, err.response?.data.errorCode as ErrorCodes);
      } else {
        closeRequest(DEFAULT_ERROR_MESSAGE);
      }
      return { error: err, success: false };
    }
  };

  const sendResetPasswordfy = async ({ newPassword, token }: SignInCredentials) => {
    try {
      openRequest();
      await http.post(`${BASE_URL}/passwordreset/update`, { token, newPassword });
      setSuccess(true);
      closeRequest('');
    } catch (err) {
      setSuccess(false);
      if (http.isAxiosError(err)) {
        const { errorCode, message } = err.response?.data;
        const closeRequestMessage = message || getErrorMessage(errorCode);

        closeRequest(closeRequestMessage, errorCode as ErrorCodes);
      } else {
        closeRequest(DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const sendContactUser = async ({ byPhone }: SignInCredentials) => {
    try {
      openRequest();
      await http.post(`${BASE_URL}/mfa/resend/${byPhone}`);
      setSuccess(true);
      closeRequest('');
    } catch (err: any) {
      setSuccess(false);
      if (http.isAxiosError(err)) {
        closeRequest(err.response?.data.message || DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const getTokenExpirationTime = async (resetPasswordToken: string) => {
    try {
      const response = await http.get<Date>(`${BASE_URL}/passwordreset/${resetPasswordToken}/tokenExpirationTime`);
      return response.data;
    } catch (err) {
      // invalid / expired token
      setSuccess(false);
      if (http.isAxiosError(err)) {
        const errorMessage = getErrorMessage(err.response?.data.errorCode);
        closeRequest(errorMessage, err.response?.data.errorCode as ErrorCodes);
      } else {
        closeRequest(DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const getUserEmailByRegistrationToken = async (registrationToken: string) => {
    try {
      const response = await http.get<string>(`${BASE_URL}/passwordreset/${registrationToken}/user-email`);
      return response.data;
    } catch (err) {
      // invalid / expired token
      setSuccess(false);
      if (http.isAxiosError(err)) {
        const errorMessage = getErrorMessage(err.response?.data.errorCode);
        closeRequest(errorMessage, err.response?.data.errorCode as ErrorCodes);
      } else {
        closeRequest(DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const completeRegistration = async ({ newPassword, token, phone }: SignInCredentials) => {
    try {
      openRequest();
      await http.post(`${BASE_URL}/passwordreset/complete-registration`, {
        registrationToken: token,
        newPassword,
        phone
      });
      setSuccess(true);
      closeRequest('');
    } catch (err) {
      setSuccess(false);
      if (http.isAxiosError(err)) {
        const errorMessage = getErrorMessage(err.response?.data.errorCode);
        closeRequest(errorMessage, err.response?.data.errorCode as ErrorCodes);
      } else {
        closeRequest(DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  return {
    login,
    sendQrPinCode,
    sendPinCode,
    sendResetPassword,
    sendResetPasswordfy,
    sendContactUser,
    loading,
    error,
    clearError,
    isSuccess,
    errorCode,
    getTokenExpirationTime,
    getUserEmailByRegistrationToken,
    completeRegistration
  };
}
